/**
 * When somebody on the floor has a reason to speak first.
 *
 * Everything else in this system is a reply. A technician answers, a
 * supervisor answers, and if the student never asks, nobody ever says
 * anything - which is not how a floor works. The person standing at the
 * tool you just scrapped a wafer on says something, and they say it whether
 * or not you turned round.
 *
 * THE KNOCK, AND WHY IT IS NOT A LINE.
 *
 * What this raises is not a sentence, it is somebody catching your eye:
 * a name, and a plain local phrase for what it is about. The sentence is
 * fetched only if the student takes it (`talkStore.offer`). Three reasons,
 * in order of how much they matter:
 *
 *   1. NO MODEL CALL UNLESS IT IS WANTED. A trigger that spent a call every
 *      time a wafer went out of spec would spend most of them on students
 *      who were already walking away.
 *   2. NOTHING ARRIVES UNASKED IN THE MIDDLE OF SOMETHING. A knock is a
 *      button at the foot of the screen; it does not take the keyboard,
 *      does not stop the walk, and does not put words on screen the student
 *      did not ask for.
 *   3. THE REASON IS TRUE WITHOUT A MODEL. "About the wafer that just came
 *      out of spec" is composed here from the journal event that caused it,
 *      so what the knock claims is a fact from the record even if the reply
 *      never gets fetched.
 *
 * WHAT MAY RAISE ONE. Only things that HAPPENED, never a judgement about
 * the student: a run out of spec, a call that was wrong, a work order with
 * an objective open and nothing being done. Somebody who is working steadily
 * and getting it right is left alone, which is also what a good floor does.
 *
 * WHO RAISES IT. The person whose tool it happened at - `cast.ts` `toolId` -
 * or the shift supervisor for anything about the work order. Only some of the
 * tools have somebody standing at them; nobody knocks from
 * DICE-15 because there is nobody there, and inventing one would be the
 * first lie this whole design has told.
 */

import { CAST, castById, castName, castShortName } from '../fab/cast'
import { getTool, type Tool } from '../fab/layout'
import type { Journal, JournalEvent } from '../learn/Journal'
import { missionState } from '../learn/missionState'
import { t } from '../i18n/i18n'
import { talkStore } from './talkStore'

export interface Knock {
  /** Cast id of whoever wants a word. */
  who: string
  /** Their name, for the button. */
  name: string
  /** What it is about, in the student's language, composed from the record. */
  about: string
  /**
   * The same thing in English, as the instruction the technician is given
   * if the student takes it. Not shown to anybody.
   */
  reason: string
  /** One knock per cause: a repeat of the same cause is not news. */
  key: string
}

/** Long enough that two bad wafers in a row are one interruption, not two. */
const COOLDOWN_MS = 90_000
/**
 * How long a work order can sit with nothing happening before the
 * supervisor mentions it. Long: a student reading a panel is working.
 */
const IDLE_MS = 5 * 60_000

let pending: Knock | null = null
let lastAt = 0
const spent = new Set<string>()
let journal: Journal | null = null
let stopJournal: (() => void) | null = null
let idleTimer: ReturnType<typeof setInterval> | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of [...listeners]) fn()
}

/** Whoever stands at this tool, if anybody does. */
const personAt = (toolId: string): string | null => CAST.find((c) => c.toolId === toolId)?.id ?? null

/**
 * Raise one, if there is room for it.
 *
 * Refused while a conversation is open - somebody mid-sentence with another
 * technician is not interrupted by a third - and refused inside the
 * cooldown, and refused for a cause already spent.
 */
function raise(who: string, about: string, reason: string, key: string): void {
  if (spent.has(key)) return
  if (talkStore.open) return
  const now = Date.now()
  if (pending || now - lastAt < COOLDOWN_MS) return
  spent.add(key)
  lastAt = now
  pending = { who, name: castShortName(who), about, reason, key }
  notify()
}

/** The tool an event happened at, for the few kinds that can raise a knock. */
function toolOf(e: JournalEvent): Tool | undefined {
  const named = 'tool' in e ? e.tool : undefined
  const id = 'toolId' in e ? e.toolId : undefined
  if (id) return getTool(id)
  if (!named) return undefined
  return (
    getTool(named) ??
    // Asset tag, or a condition model's label leading with one.
    CAST.map((c) => c.toolId)
      .filter((x): x is string => !!x)
      .map((x) => getTool(x))
      .find((tool) => tool && (named === tool.assetTag || named.startsWith(`${tool.assetTag} `)))
  )
}

/**
 * The shift that untooled events belong to.
 *
 * A `wafer` event carries no tool AT ALL - it is recorded by the panel the
 * student is standing at, and the one it belongs to is whichever shift was
 * open when it ran. That is the rule `stationSummary`, `currentShiftEvents`
 * and `work.ts` all use, and forgetting it here is why the first version of
 * this file never raised a knock: the only event that can be out of spec is
 * the only one with nothing on it to say where it happened.
 */
let openShift: Tool | undefined

/**
 * A new journal event. Only the two that are worth a word: a run that did
 * not make spec, and a call that was wrong. Both are facts about the
 * record, and both are the business of whoever is standing at that tool.
 */
function onEvent(e: JournalEvent): void {
  if (e.kind === 'shiftStart') openShift = toolOf(e) ?? openShift
  if (e.kind === 'visit') openShift = toolOf(e) ?? openShift
  const at = toolOf(e) ?? openShift
  if (!at) return
  const who = personAt(at.id)
  if (!who) return
  if ((e.kind === 'wafer' || e.kind === 'product') && !e.inSpec) {
    raise(
      who,
      t('talk.knock.OutOfSpec', { tag: at.assetTag }),
      `The last run at ${at.assetTag} came out OUT OF SPEC, and you saw it. Say something about it.`,
      `spec:${e.at}`,
    )
    return
  }
  if (e.kind === 'call' && !e.correct) {
    raise(
      who,
      t('talk.knock.WrongCall', { tag: at.assetTag }),
      `They called the fault at ${at.assetTag} wrong, and the tool is still down. Say something about it.`,
      `call:${e.at}`,
    )
  }
}

/**
 * Nothing happening, with a work order open.
 *
 * The supervisor's one trigger, and the only one here that is about the
 * student rather than about a machine - which is why it is the slowest and
 * the quietest: five minutes of nothing recorded anywhere, an objective
 * still open, and he mentions it once.
 */
function onIdle(): void {
  if (!journal) return
  const progress = missionState.progress()
  if (!progress) return
  const openObjective = progress.mission.objectives.findIndex((_, i) => !progress.met[i])
  if (openObjective < 0) return
  const last = journal.events[journal.events.length - 1]?.at ?? 0
  if (!last || Date.now() - last < IDLE_MS) return
  const kevin = CAST.find((c) => c.toolId === null)?.id
  if (!kevin) return
  raise(
    kevin,
    t('talk.knock.Stalled'),
    'They have a work order open with an objective still to do and nothing has been recorded for a while. Ask what they are stuck on and point at the next step.',
    `idle:${Math.round(last / 60_000)}`,
  )
}

export const talkWatch = {
  get pending(): Knock | null {
    return pending
  },

  /**
   * Start watching. Wired once by the composition root; the journal is the
   * only source, so nothing here can know something the record does not.
   */
  start(record: Journal): void {
    if (stopJournal) return
    journal = record
    let seen = record.events.length
    stopJournal = record.subscribe(() => {
      const fresh = record.events.slice(seen)
      seen = record.events.length
      for (const e of fresh) onEvent(e)
    })
    idleTimer = setInterval(onIdle, 60_000)
  },

  stop(): void {
    stopJournal?.()
    stopJournal = null
    if (idleTimer) clearInterval(idleTimer)
    idleTimer = null
    journal = null
  },

  /**
   * The student took it: open that conversation and let them speak first.
   * Clearing `pending` before the call so a slow reply cannot leave the
   * button sitting there looking unpressed.
   */
  take(): void {
    const knock = pending
    pending = null
    notify()
    if (!knock || !castById(knock.who)) return
    void talkStore.offer(knock.who, knock.reason)
  },

  /** The student ignored it. The cause is spent either way. */
  dismiss(): void {
    if (!pending) return
    pending = null
    notify()
  },

  /** For a harness, and for a session that starts over. */
  clear(): void {
    pending = null
    lastAt = 0
    openShift = undefined
    spent.clear()
    notify()
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}

/** The name as the knock button says it, exported for the panel's own copy. */
export const knockName = (who: string): string => castName(who)
